/**
 * Gift matching pool actions
 *
 * Pool links shown in a campaign's gift matching section: a notice on a pooled gift match, and the
 * system-only action that starts a pool from a gift match. Both open the pool screen in a new tab.
 */

import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Button, Message } from 'semantic-ui-react';
import { I18n, Translate } from 'react-redux-i18n';
import { SystemOnlyIcon, SystemOnlyPopup } from '../../common/SystemOnlyBadge';
import { isSystemReadWrite, Session } from '../../../modules/session';
import { systemSettingsRoutes } from '../../../pages/systemSettings/constants';
import { hasPageConditions } from '../../../pages/systemSettings/giftMatchingPools/helpers';
import Styles from './GiftMatchingPoolActions.module.css';

export const giftMatchingPoolActionKeys = {
  POOLED: 'template.page.content.giftMatching.isPoolEntry.label',
  MANAGE: 'template.page.content.giftMatching.isPoolEntry.manage',
  CREATE_LINKED: 'template.page.content.giftMatching.createLinked.label',
  PAGE_CONDITIONS:
    'template.page.content.giftMatching.createLinked.pageConditions',
  NEW_TAB: 'template.page.content.giftMatching.createLinked.newTab'
};

const useCanManagePools = () => {
  const session = useSelector((state: { session: Session }) => state.session);
  return !!session && isSystemReadWrite(session);
};

interface PooledGiftMatchNoticeProps {
  poolId?: number | null;
}

export const PooledGiftMatchNotice: React.FC<PooledGiftMatchNoticeProps> = ({
  poolId
}) => {
  const canManage = useCanManagePools() && !!poolId;
  return (
    <Message warning visible className={Styles.notice}>
      <p>
        <Translate value={giftMatchingPoolActionKeys.POOLED} />
      </p>
      {canManage && (
        <p className={Styles.manage}>
          <SystemOnlyPopup>
            <Link
              to={`${systemSettingsRoutes.GIFT_MATCHING_POOLS_EDIT}/${poolId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Translate value={giftMatchingPoolActionKeys.MANAGE} />
              <span className={Styles.srOnly}>
                {` ${I18n.t(giftMatchingPoolActionKeys.NEW_TAB)}`}
              </span>
              <SystemOnlyIcon />
            </Link>
          </SystemOnlyPopup>
        </p>
      )}
    </Message>
  );
};

interface CreateLinkedGiftMatchActionProps {
  campaignId?: number | null;
  conditions?: { key: string }[] | null;
  giftMatchingId?: number | null;
}

export const CreateLinkedGiftMatchAction: React.FC<
  CreateLinkedGiftMatchActionProps
> = ({ campaignId, conditions, giftMatchingId }) => {
  const canManage = useCanManagePools();
  const [showBlocked, setShowBlocked] = useState(false);

  if (!canManage || !campaignId || !giftMatchingId) {
    return null;
  }

  const blocked = hasPageConditions(conditions);
  const label = I18n.t(giftMatchingPoolActionKeys.CREATE_LINKED);

  return (
    <div className={Styles.action}>
      <SystemOnlyPopup>
        {blocked ? (
          <Button
            type="button"
            basic
            size="small"
            aria-expanded={showBlocked}
            onClick={() => setShowBlocked(true)}
          >
            {label}
            <SystemOnlyIcon />
          </Button>
        ) : (
          <Link
            className="ui basic small button"
            to={`${systemSettingsRoutes.GIFT_MATCHING_POOLS_CREATE}?campaignId=${campaignId}&giftMatchingId=${giftMatchingId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {label}
            <span className={Styles.srOnly}>
              {` ${I18n.t(giftMatchingPoolActionKeys.NEW_TAB)}`}
            </span>
            <SystemOnlyIcon />
          </Link>
        )}
      </SystemOnlyPopup>
      {blocked && showBlocked && (
        <Message warning visible role="alert" className={Styles.message}>
          <Translate value={giftMatchingPoolActionKeys.PAGE_CONDITIONS} />
        </Message>
      )}
    </div>
  );
};
